import { Box } from "@mui/material";
import React, { useRef, useState } from "react";

import { AddIcon } from "assets/icons";
import { GroundlightButton } from "new-components/buttons/GroundlightButton";
import { CanvasAnnotation } from "types/imageAnnotations";

import { EditableImageCard } from "../../EditableImageCard/EditableImageCard";

import { ImageAnnotatorModal } from "./ImageAnnotatorModal/ImageAnnotatorModal";

type ImageUploaderProps = {
  originalImage?: string;
  onSaveAnnotatedImage?: (file: File | null) => void;
  hasImage?: boolean;
  onDeleteBackendImage?: () => void;
  selectedImage: File | null;
  setSelectedImage: React.Dispatch<React.SetStateAction<File | null>>;
  imageToAnnotate: string | null;
  setImageToAnnotate: React.Dispatch<React.SetStateAction<string | null>>;
  annotatedImage: File | null;
  setAnnotatedImage: React.Dispatch<React.SetStateAction<File | null>>;
  savedAnnotations: CanvasAnnotation[];
  setSavedAnnotations: React.Dispatch<React.SetStateAction<CanvasAnnotation[]>>;
  annotations: CanvasAnnotation[];
  setAnnotations: React.Dispatch<React.SetStateAction<CanvasAnnotation[]>>;
};

export function ImageUploader({
  originalImage,
  onSaveAnnotatedImage,
  hasImage,
  onDeleteBackendImage,
  selectedImage,
  setSelectedImage,
  imageToAnnotate,
  setImageToAnnotate,
  annotatedImage,
  setAnnotatedImage,
  savedAnnotations,
  setSavedAnnotations,
  annotations,
  setAnnotations,
}: ImageUploaderProps) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleAnnotatedImageUpdate = (file: File | null) => {
    setAnnotatedImage(file);
    onSaveAnnotatedImage?.(file);
  };

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  const [isOpen, setIsOpen] = useState(false);

  const closeModal = () => {
    setIsOpen(false);
    setAnnotations(savedAnnotations);
  };

  const handleFileChange: React.ChangeEventHandler<HTMLInputElement> = (event) => {
    const file = event.target.files?.item(0) ?? null;
    setImageToAnnotate(file ? URL.createObjectURL(file) : null);
    setSelectedImage(file);
    setIsOpen(true);
  };

  const viewedImageUrl =
    (annotatedImage ? URL.createObjectURL(annotatedImage) : null) ?? imageToAnnotate ?? originalImage ?? null;

  const handleEditInMemoryImage = () => {
    // Use originalImage if selectedImage is null (for existing notes)
    setImageToAnnotate(selectedImage ? URL.createObjectURL(selectedImage) : originalImage ?? null);
    setIsOpen(true);
  };

  const hadleDeleteInMemoryAnnotatedImage = () => {
    setImageToAnnotate(null);
    setAnnotatedImage(null);
    onSaveAnnotatedImage?.(null);
    setAnnotations([]);
    setSavedAnnotations([]);
  };

  return (
    <>
      <Box
        width="100%"
        height="100%"
        sx={{
          background: "white",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        {hasImage && viewedImageUrl ? (
          <EditableImageCard
            imageUrl={viewedImageUrl}
            canModify
            canViewMaximizedImage={false}
            onEdit={!onDeleteBackendImage ? handleEditInMemoryImage : undefined}
            onDelete={onDeleteBackendImage ?? hadleDeleteInMemoryAnnotatedImage}
          />
        ) : (
          <>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              onClick={(event) => {
                // resets input value to allow selecting same file two times in a row
                // eslint-disable-next-line no-param-reassign
                event.currentTarget.value = "";
              }}
              accept="image/*"
              hidden
            />

            <GroundlightButton endIcon={AddIcon} onClick={handleButtonClick}>
              Upload image
            </GroundlightButton>
          </>
        )}
      </Box>

      <ImageAnnotatorModal
        open={isOpen}
        imageUrl={imageToAnnotate}
        onClose={closeModal}
        onCancel={closeModal}
        annotations={annotations}
        setAnnotations={setAnnotations}
        onSaveAnnotatedImage={(annotatedImage) => {
          handleAnnotatedImageUpdate(annotatedImage);
          setSavedAnnotations(annotations);
          setIsOpen(false);
        }}
      />
    </>
  );
}
